// ---------------------------------------------------------------------------
// KgGenerationForm � types, constants, and props.
// ---------------------------------------------------------------------------

export type KgDomain =
  | 'Technology'
  | 'Healthcare'
  | 'Finance'
  | 'Manufacturing';

export type KgExpertiseBias = 'mixed' | 'mostly-expert' | 'mostly-novice';

export const KG_DOMAINS: { value: KgDomain; label: string }[] = [
  { value: 'Technology', label: 'Technology' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Manufacturing', label: 'Manufacturing' },
];

// Tone and style vocabularies. These MUST stay in sync with the generator:
// VALID_TONES / VALID_STYLES in
// src/worker-py/api/src/worker_py_api/models/synthesizer_requests.py, which in
// turn mirror TONE_BEHAVIORS / STYLE_BEHAVIORS in the kg_synthesizer_lib
// persona module. The API rejects any value outside those tuples, so offering
// an extra option here does not degrade output — it fails the run.
export const KG_TONES = [
  'direct',
  'casual',
  'professional',
  'empathetic',
  'persuasive',
  'humorous',
  'skeptical',
  'enthusiastic',
  'anxious',
] as const;

export const KG_STYLES = [
  'concise',
  'chatty',
  'elaborative',
  'boring',
  'action-oriented',
  'anecdotal',
] as const;

export const KG_EXPERTISE_BIAS_OPTIONS: {
  value: KgExpertiseBias;
  label: string;
}[] = [
  { value: 'mixed', label: 'Mixed' },
  { value: 'mostly-expert', label: 'Mostly expert' },
  { value: 'mostly-novice', label: 'Mostly novice' },
];

/** Snapshot of all form values. */
export interface KgGenerationFormValues {
  // Context � exactly one of domain or scenario is meaningful per contextMode
  contextMode: 'domain' | 'scenario';
  domain: KgDomain;
  scenario: string;

  // Scale
  totalMessages: number;
  minUsers: number;
  maxUsers: number;
  numTopics: number;
  numPhases: number;

  // Advanced
  noiseRatio: number;
  tones: string[];
  styles: string[];
  expertiseBias: KgExpertiseBias;

  // Generation strategy. Episodes commit 3-6 turns at once, so the produced
  // message count can exceed totalMessages.
  useBatchedEpisodes: boolean;
  useAgent: boolean;

  // Share of messages that should come from agent exchanges. Spacing between
  // exchanges is derived from this, so raising it genuinely produces more.
  agentShare: number;
}

export interface KgGenerationFormProps {
  values: KgGenerationFormValues;
  onChange: (values: KgGenerationFormValues) => void;
  disabled?: boolean;
}
